import { useProjectStore } from '@talon/web/src/context';
import { Box, ResponsiveContext, Text, TextProps } from 'grommet';
import { Channel, DocumentText, Group, Home, IconProps, Resources, UserManager, Video } from 'grommet-icons';
import { kebabCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { projectSection } from '../../utils/projectPath';
import { AnchorLink, useIsActivePath } from './AnchorLink';
import { MenuWithChildren } from './MenuWithChildren';
import { phaseLabel, projectMenu } from './projectMenu';

export type AgencyMenusProps = {
  textColor?: TextProps['color'];
  iconColor?: IconProps['color'];
  /** Talent managers don't hire other talent managers */
  showMarketplace?: boolean;
  /** Fold the global links into More even above laptop width */
  foldLinks?: boolean;
};

export const AgencyMenus = observer(
  ({ textColor = 'accent-1', iconColor = 'accent-1', showMarketplace = true, foldLinks }: AgencyMenusProps) => {
    /** Context **/
    const navigate = useNavigate();
    const location = useLocation();
    const projectStore = useProjectStore();
    const size = useContext(ResponsiveContext);
    const inBar = size !== 'small';

    /** State **/
    const projectPath = useMemo(
      () => `/project/${kebabCase(projectStore.currentProject?.name)}`,
      [projectStore.currentProject]
    );

    const iconSize = '22px';

    /** Render **/

    const section = projectSection(location.pathname);
    const active = {
      performers: useIsActivePath('/performers'),
      marketplace: useIsActivePath('/marketplace'),
      resources: useIsActivePath('/resources'),
    };

    const menuItems = Object.entries(projectMenu).map(([phase, pages]) => ({
      label: phaseLabel(phase),
      icon:
        phase === 'pre-pro' ? (
          <Home size={iconSize} color={iconColor} />
        ) : phase === 'production' ? (
          <Video size={iconSize} color={iconColor} />
        ) : phase === 'post-production' ? (
          <Channel size={iconSize} color={iconColor} />
        ) : (
          <DocumentText size={iconSize} color={iconColor} />
        ),
      items: pages.map((page) => ({
        label: page.label,
        onClick: () => navigate(page.section ? `${projectPath}/${page.section}` : projectPath),
        active: page.section === section,
      })),
    }));

    const globalLinks = [
      { to: '/performers', label: 'Performers', icon: Group, active: active.performers },
      { to: '/marketplace', label: 'Marketplace', icon: UserManager, active: active.marketplace },
      { to: '/resources', label: 'Resources', icon: Resources, active: active.resources },
    ].filter((link) => showMarketplace || link.to !== '/marketplace');

    return (
      <>
        {projectStore.currentProject?.name &&
          menuItems.map((menu) => (
            <MenuWithChildren
              key={menu.label}
              label={menu.label}
              icon={menu.icon}
              items={menu.items}
              textColor={textColor}
              active={menu.items.some((item) => item.active)}
            />
          ))}

        {/* Between the project's phases and the links that aren't about one project */}
        {inBar && projectStore.currentProject?.name && (
          <Box
            width="1px"
            height="24px"
            flex={false}
            margin={{ horizontal: 'small' }}
            style={{ background: 'rgba(255, 255, 255, 0.35)' }}
          />
        )}

        {size === 'laptop' || (foldLinks && inBar) ? (
          // Laptop widths don't fit every link in the bar
          <MenuWithChildren
            label="More"
            items={globalLinks.map((link) => ({
              label: link.label,
              onClick: () => navigate(link.to),
              active: link.active,
            }))}
            textColor={textColor}
            active={globalLinks.some((link) => link.active)}
          />
        ) : (
          <Box direction={inBar ? 'row' : 'column'} gap="medium" align={inBar ? 'center' : 'start'}>
            {globalLinks.map(({ to, label, icon: Icon, active: linkActive }) => (
              <AnchorLink
                key={to}
                to={to}
                label={
                  <Text size="1rem" weight={linkActive ? 600 : 500} color={textColor}>
                    {label}
                  </Text>
                }
                icon={<Icon size={iconSize} color={iconColor} />}
                alignSelf="center"
              />
            ))}
          </Box>
        )}
      </>
    );
  }
);
